import { useState, useEffect } from 'react';
import Alert from '../../global/Alert/Alert';
import { encodeData, encodeFiltersToParams } from '../../utils/encodeUtils';
import SharedButton from '../../global/Button/Button';
import { userAppContext } from '../../Context/Context.jsx';
import { getFilters, deleteFilter, updateFilter } from '../../services/savedFiltersService';
import { UserContext } from '../../types.js';
import { SavedSearch, Filter, ProfileFavoritesProps, TabType } from './types';
import {
  CONTEXT_PARAMS,
  filterColumnFilters,
  determineSearchType,
  storeTemporaryData,
  getEmptyMessage,
  getTabLabel,
} from './favoritesUtils';
import TabButton from './TabButton';
import SearchCard from './SearchCard';

const ProfileFavorites = ({ favoritesClass }: ProfileFavoritesProps) => {
  const [savedSearches, setSavedSearches] = useState<SavedSearch[]>([]);
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editId, setEditId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [showUpdateAlert, setShowUpdateAlert] = useState(false);
  const [activeTab, setActiveTab] = useState<TabType>('filters');
  const [showScoreDeleteAlert, setShowScoreDeleteAlert] = useState(false);

  const { isAuthenticated } = userAppContext() as unknown as UserContext;

  useEffect(() => {
    if (isAuthenticated) {
      loadSavedSearches();
    }
  }, [isAuthenticated]);

  const loadSavedSearches = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await getFilters();
      const cloudFilters = response.filters || [];

      const searches = cloudFilters.map((filter: any) => ({
        id: filter.filterId,
        table: filter.type === 'score' ? 'Score' : filter.isEvidence ? 'Evidence' : 'Summary',
        filters: filter.filters,
        searchQuery: filter.searchQuery,
        savedAt: filter.createdAt,
        name: filter.name,
        type: determineSearchType(filter),
        columnOrder: filter.columnOrder,
        columnVisibilityModel: filter.columnVisibilityModel,
        searchContext: filter.searchContext,
        sourceWeights: filter.sourceWeights,
        isLocal: false,
      }));

      const localColumnSelections = loadLocalColumnSelections();
      const localScores = loadLocalScores();

      setSavedSearches([...searches, ...localColumnSelections, ...localScores]);
    } catch (err) {
      console.error('Error loading saved searches:', err);
      setError('Failed to load saved searches');
    } finally {
      setIsLoading(false);
    }
  };

  const loadLocalColumnSelections = (): SavedSearch[] => {
    const columnSelections: SavedSearch[] = [];
    const keys = Object.keys(localStorage);

    keys.forEach((key: string) => {
      if (key.startsWith('columnSelection_') || key.startsWith('completeSearch_')) {
        try {
          const selection = JSON.parse(localStorage.getItem(key) || '');
          columnSelections.push({
            id: key,
            table: selection.searchContext?.isEvidence ? 'Evidence' : 'Summary',
            filters: selection.columnFilters || selection.columnVisibilityModel || [],
            searchQuery: selection.searchQuery || null,
            savedAt: selection.savedAt,
            name: selection.name,
            type: key.startsWith('completeSearch_') ? 'complete' : 'columns',
            columnOrder: selection.columnOrder,
            columnVisibilityModel: selection.columnVisibilityModel,
            searchContext: selection.searchContext,
            isLocal: true,
          });
        } catch (error) {
          console.error('Error parsing saved selection:', error);
        }
      }
    });

    return columnSelections.sort((a, b) => new Date(b.savedAt).getTime() - new Date(a.savedAt).getTime());
  };

  const loadLocalScores = (): SavedSearch[] => {
    const scores: SavedSearch[] = [];
    const keys = Object.keys(localStorage);

    keys.forEach((key: string) => {
      if (key.startsWith('savedScore_')) {
        try {
          const scoreData = JSON.parse(localStorage.getItem(key) || '');
          scores.push({
            id: key,
            table: 'Score',
            filters: [],
            searchQuery: null,
            savedAt: scoreData.savedAt,
            name: scoreData.name,
            type: 'score',
            sourceWeights: scoreData.sourceWeights,
            searchContext: scoreData.searchContext,
            isLocal: true,
          });
        } catch (error) {
          console.error('Error parsing saved score:', error);
        }
      }
    });

    return scores.sort((a, b) => new Date(b.savedAt).getTime() - new Date(a.savedAt).getTime());
  };

  const getFilteredSearches = (): SavedSearch[] => {
    return savedSearches.filter((search: SavedSearch) => {
      if (activeTab === 'filters') {
        if (search.type === 'filter') return true;
        if (!search.type && Array.isArray(search.filters)) {
          const columnFilters = search.filters.filter((f: Filter) => !CONTEXT_PARAMS.includes(f.name));
          return columnFilters.length > 0 && !search.searchQuery;
        }
        return false;
      }
      if (activeTab === 'searches') return search.type === 'search' || search.type === 'complete';
      if (activeTab === 'columns') return search.type === 'columns';
      if (activeTab === 'scores') return search.type === 'score';
      return false;
    });
  };

  const filteredSearches = getFilteredSearches();

  const buildSearchUrl = (search: SavedSearch): string | null => {
    try {
      const url = new URL(`${window.location.origin}/search`);
      const filters = search.filters || [];

      if (search.searchQuery) {
        const searchParams = new URLSearchParams(search.searchQuery);
        searchParams.forEach((value, key) => url.searchParams.set(key, value));

        const columnFilters = filterColumnFilters(filters);
        if (columnFilters.length > 0) {
          url.searchParams.set('f', encodeData(columnFilters));
        }
        return url.href;
      }

      if (filters.length === 0) return null;

      const getFilterValue = (name: string, defaultValue: string) =>
        filters.find((f: Filter) => f.name === name)?.value || defaultValue;

      url.searchParams.set('view', getFilterValue('view', 'ALL'));
      url.searchParams.set('tab', getFilterValue('tab', 'GDA'));
      url.searchParams.set('source', getFilterValue('source', 'ALL'));
      url.searchParams.set('idents', getFilterValue('idents', 'ALL'));

      const columnFilters = filterColumnFilters(filters);
      if (columnFilters.length > 0) {
        const filterParams = encodeFiltersToParams(columnFilters);
        filterParams.forEach((value, key) => url.searchParams.set(key, value));
      }

      return url.href;
    } catch (error) {
      console.error('Error building search URL:', error);
      return null;
    }
  };

  const handleNavigateToSearch = (search: SavedSearch) => {
    if (search.type === 'complete' && (search.columnVisibilityModel || search.columnOrder)) {
      storeTemporaryData(
        search.id,
        {
          columnVisibilityModel: search.columnVisibilityModel,
          columnOrder: search.columnOrder,
          name: search.name,
        },
        true,
      );
    }

    const targetUrl = search.searchQuery
      ? buildSearchUrl(search)
      : buildSearchUrl({
          ...search,
          filters: (search.filters || []).map((f: Filter) => ({
            ...f,
            isEvidence: search.table === 'Evidence',
          })),
        });

    if (targetUrl) {
      const url = new URL(targetUrl);
      if (search.type === 'complete' && search.searchQuery) {
        url.searchParams.set('loadComplete', search.id);
      }
      window.open(url.href.replace(window.location.origin, ''), '_blank');
    }
  };

  const handleNavigateWithColumns = (search: SavedSearch) => {
    if (!search.searchContext) return;

    const { view, tab, source } = search.searchContext;
    const url = new URL(`${window.location.origin}/search`);
    const isComplete = search.type === 'complete';

    // Set base parameters
    if (isComplete && search.searchQuery) {
      const searchParams = new URLSearchParams(search.searchQuery);
      searchParams.forEach((value, key) => url.searchParams.set(key, value));
    } else {
      url.searchParams.set('view', view || 'ALL');
      url.searchParams.set('tab', tab || 'GDA');
      url.searchParams.set('source', source || 'ALL');
      url.searchParams.set('idents', 'ALL');
    }

    // Add filters for complete searches
    if (isComplete && search.filters?.length) {
      const columnFilters = filterColumnFilters(search.filters);
      if (columnFilters.length > 0) {
        url.searchParams.set('f', encodeData(columnFilters));
      }
    }

    // Add isEvidence for column-only searches
    if (!isComplete && search.searchContext.isEvidence !== undefined) {
      url.searchParams.set('isEvidence', search.searchContext.isEvidence.toString());
    }

    // Store temporary data
    storeTemporaryData(
      search.id,
      {
        columnVisibilityModel: search.columnVisibilityModel || search.filters,
        columnOrder: search.columnOrder,
        name: search.name,
      },
      isComplete,
    );

    url.searchParams.set(isComplete ? 'loadComplete' : 'loadColumns', search.id);
    window.open(url.href.replace(window.location.origin, ''), '_blank');
  };

  const handleNavigateWithScore = (search: SavedSearch) => {
    if (!search.searchContext || !search.sourceWeights) return;

    const { view, tab, source } = search.searchContext;
    const url = new URL(`${window.location.origin}/search`);

    url.searchParams.set('view', view || 'ALL');
    url.searchParams.set('tab', tab || 'GDA');
    url.searchParams.set('source', search.sourceWeights.length > 1 ? 'ALL' : source || 'ALL');
    url.searchParams.set('idents', 'ALL');

    sessionStorage.setItem('sourceWeights', JSON.stringify(search.sourceWeights));

    window.open(url.href.replace(window.location.origin, ''), '_blank');
  };

  const handleDeleteSearch = async (searchId: string) => {
    try {
      const searchToDelete = savedSearches.find(s => s.id === searchId);

      if (searchToDelete?.isLocal) {
        localStorage.removeItem(searchId);
      } else {
        await deleteFilter(searchId);
      }

      const updatedSearches = savedSearches.filter(s => s.id !== searchId);
      setSavedSearches(updatedSearches);

      setShowDeleteAlert(true);
      setTimeout(() => setShowDeleteAlert(false), 3000);
    } catch (error) {
      console.error('Error deleting search:', error);
      setError('Failed to delete search');
    }
  };

  const handleClearAllSearches = async () => {
    try {
      const searchesToDelete = filteredSearches;

      const localItems = searchesToDelete.filter(s => s.isLocal);
      const cloudItems = searchesToDelete.filter(s => !s.isLocal);

      localItems.forEach(item => localStorage.removeItem(item.id));

      if (cloudItems.length > 0) {
        await Promise.all(cloudItems.map(search => deleteFilter(search.id)));
      }

      const remainingSearches = savedSearches.filter(s => !searchesToDelete.find(d => d.id === s.id));
      setSavedSearches(remainingSearches);
      setShowDeleteAlert(true);
      setTimeout(() => setShowDeleteAlert(false), 3000);
    } catch (error) {
      console.error('Error clearing all searches:', error);
      setError('Failed to clear all searches');
    }
  };

  if (!isAuthenticated) {
    return (
      <div className={favoritesClass} id="favorites">
        <h2>Saved Searches</h2>
        <div className="wrap-input100" />
        <div style={{ padding: '20px 0' }}>
          <p className="label-input100" style={{ color: '#999' }}>
            Please log in to see your saved searches.
          </p>
        </div>
      </div>
    );
  }

  const handleSaveEdit = async (filterId: string) => {
    if (!editName.trim()) return;

    try {
      await updateFilter(filterId, { name: editName });

      setSavedSearches(prev => prev.map(s => (s.id === filterId ? { ...s, name: editName } : s)));

      setEditId(null);
      setEditName('');
      setShowUpdateAlert(true);
      setTimeout(() => setShowUpdateAlert(false), 3000);
    } catch (error) {
      console.error('Error updating filter name:', error);
      setError('Failed to update filter name');
    }
  };

  return (
    <>
      {showDeleteAlert && <Alert message="Search deleted successfully" type="success" />}
      {showUpdateAlert && <Alert message="Filter saved successfully!" type="success" />}
      {error && <Alert message={error} type="error" />}

      <div className={favoritesClass} id="favorites">
        <h2>Saved Searches</h2>

        {/* Tabs */}
        <div
          style={{
            display: 'flex',
            gap: '8px',
            marginBottom: '20px',
            borderBottom: '2px solid #e5e7eb',
          }}
        >
          <TabButton active={activeTab === 'filters'} onClick={() => setActiveTab('filters')}>
            Saved filters
          </TabButton>
          <TabButton active={activeTab === 'searches'} onClick={() => setActiveTab('searches')}>
            Saved searches
          </TabButton>
          <TabButton active={activeTab === 'columns'} onClick={() => setActiveTab('columns')}>
            Saved columns
          </TabButton>
          {process.env.REACT_APP_SHOW_SCORE_BUTTON === 'true' && (
            <TabButton active={activeTab === 'scores'} onClick={() => setActiveTab('scores')}>
              Saved scores
            </TabButton>
          )}
        </div>

        <div className="wrap-input100" />

        {isLoading ? (
          <div style={{ padding: '40px 0', textAlign: 'center' }}>
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden"></span>
            </div>
            <p style={{ marginTop: '16px', color: '#666' }}>Loading your saved searches...</p>
          </div>
        ) : filteredSearches.length === 0 ? (
          <div style={{ padding: '20px 0' }}>
            <p className="label-input100" style={{ color: '#999' }}>
              {getEmptyMessage(activeTab)}
            </p>
          </div>
        ) : (
          <>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <p className="input100">
                {getTabLabel(activeTab)}: {filteredSearches.length}
              </p>
              {filteredSearches.length > 0 && (
                <SharedButton
                  id="clearAll"
                  size="xs"
                  onClick={handleClearAllSearches}
                  type="button"
                  className="BtnAccion mx-0 mb-2"
                  style={{ height: '30px', width: '8em', fontSize: '12px' }}
                >
                  Clear All
                </SharedButton>
              )}
            </div>

            <div style={{ marginTop: '20px' }}>
              {filteredSearches.map(search => (
                <SearchCard
                  key={search.id}
                  search={search}
                  activeTab={activeTab}
                  editId={editId}
                  editName={editName}
                  onEditChange={setEditName}
                  onSaveEdit={handleSaveEdit}
                  onCancelEdit={() => {
                    setEditId(null);
                    setEditName('');
                  }}
                  onStartEdit={(id, name) => {
                    setEditId(id);
                    setEditName(name);
                  }}
                  onNavigate={handleNavigateToSearch}
                  onNavigateWithColumns={handleNavigateWithColumns}
                  onNavigateWithScore={handleNavigateWithScore}
                  onDelete={handleDeleteSearch}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default ProfileFavorites;
